<template>
  <b-card
    v-if="visible"
    no-body
    class="mt-2 mb-2 shadow-sm"
    :class="`border-left-${getStatusVariant()}`"
    style="border-left-width: 4px !important"
  >
    <b-card-header
      class="p-3 bg-light cursor-pointer"
      @click="toggleExpand"
      style="cursor: pointer"
    >
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center flex-grow-1">
          <b-badge
            :variant="getStatusVariant()"
            class="mr-3 d-flex align-items-center justify-content-center"
            style="width: 36px; height: 36px; border-radius: 8px"
          >
            <i class="fa fa-shield-alt"></i>
          </b-badge>

          <div class="flex-grow-1">
            <h6 class="mb-1 font-weight-bold text-truncate">
              {{ warrantyProduct.productName || 'Gói bảo hành' }}
              <span v-if="warrantyProduct.warrantyMonthNo" class="text-muted">
                • {{ warrantyProduct.warrantyMonthNo }} tháng
              </span>
            </h6>
            <small :class="`text-${getStatusVariant()}`">
              {{ getStatusText() }}
              <span v-if="remainingTime" class="text-muted"
                >• {{ remainingTime }}</span
              >
            </small>
          </div>
        </div>

        <div class="d-flex align-items-center ml-2">
          <b-button
            v-if="showDismiss"
            variant="link"
            class="text-muted p-0 mr-2"
            size="sm"
            title="Bỏ gói bảo hành"
            @click.stop="openRemoveConfirm"
          >
            <b-icon icon="x-lg" font-scale="1.1" />
          </b-button>
          <b-icon
            :icon="isExpanded ? 'chevron-up' : 'chevron-down'"
            class="ml-1"
          ></b-icon>
        </div>
      </div>
    </b-card-header>

    <b-collapse :visible="isExpanded">
      <b-card-body class="bg-light py-2 pl-2 pr-3">
        <ul class="warranty-detail-list mb-0">
          <li class="mb-2 d-flex align-items-start">
            <i class="fa fa-check-circle text-success mr-2 mt-1"></i>
            <div class="flex-grow-1">
              <span class="font-weight-bold d-block mb-1"
                >Tên gói bảo hành</span
              >
              <span class="text-dark">{{
                warrantyProduct.productName || '—'
              }}</span>
            </div>
          </li>
          <li class="mb-2 d-flex align-items-start">
            <i class="fa fa-check-circle text-success mr-2 mt-1"></i>
            <div class="flex-grow-1">
              <span class="font-weight-bold d-block mb-1">Mã sản phẩm</span>
              <span class="text-dark">{{
                warrantyProduct.productCode || '—'
              }}</span>
            </div>
          </li>
          <li class="mb-2 d-flex align-items-start">
            <i class="fa fa-check-circle text-success mr-2 mt-1"></i>
            <div class="flex-grow-1">
              <span class="font-weight-bold d-block mb-1"
                >Thời hạn bảo hành</span
              >
              <span class="text-dark">
                {{
                  warrantyProduct.warrantyMonthNo
                    ? `${warrantyProduct.warrantyMonthNo} tháng`
                    : '—'
                }}
              </span>
            </div>
          </li>
        </ul>
      </b-card-body>
    </b-collapse>
  </b-card>
</template>

<script>
import moment from 'moment'
import { showDeleteAlert } from '@/utils/sweet-alert2'

export default {
  name: 'WarrantyPackageInfoCard',
  props: {
    warrantyProduct: {
      type: Object,
      default: () => ({}),
    },
    purchaseDate: {
      type: String,
      default: null,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    showDismiss: {
      type: Boolean,
      default: false,
    },
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: this.defaultExpanded,
    };
  },
  computed: {
    calculatedExpiryDate() {
      // Tính ngày hết hạn từ purchaseDate + warrantyMonthNo
      if (!this.purchaseDate || !this.warrantyProduct.warrantyMonthNo) {
        return null;
      }

      try {
        // Parse purchaseDate (format: DD/MM/YYYY) và thêm số tháng bảo hành
        const purchaseMoment = moment(this.purchaseDate, 'DD/MM/YYYY', true);
        
        if (!purchaseMoment.isValid()) {
          return null;
        }

        const expiryMoment = purchaseMoment.clone().add(
          this.warrantyProduct.warrantyMonthNo,
          'months',
        );

        return expiryMoment.toDate();
      } catch (error) {
        return null;
      }
    },
    remainingTime() {
      const expiry = this.calculatedExpiryDate;
      if (!expiry) return null;

      const expiryMoment = moment(expiry);
      const nowMoment = moment();
      const diffDays = expiryMoment.diff(nowMoment, 'days');

      if (diffDays < 0) return 'Đã hết hạn';
      if (diffDays === 0) return 'Hết hạn hôm nay';

      const duration = moment.duration(expiryMoment.diff(nowMoment));
      const months = Math.floor(duration.asMonths());
      const days = duration.days();

      if (months > 0) {
        return `${months} tháng ${days} ngày`;
      }
      return `${diffDays} ngày`;
    },
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      this.$emit('toggle', this.isExpanded);
    },

    openRemoveConfirm() {
      showDeleteAlert(
        'Xác nhận',
        'Bạn muốn xoá gói bảo hành khỏi phiếu bảo hành?',
        null,
        () => this.$emit('dismiss'),
      )
    },

    getStatusVariant() {
      const expiry = this.calculatedExpiryDate;
      if (!expiry) return 'success';

      const expiryMoment = moment(expiry);
      const diffDays = expiryMoment.diff(moment(), 'days');

      if (diffDays < 0) return 'danger';
      if (diffDays <= 30) return 'warning';
      return 'success';
    },

    getStatusText() {
      const expiry = this.calculatedExpiryDate;
      if (!expiry) return 'Đang hoạt động';

      const expiryMoment = moment(expiry);
      const diffDays = expiryMoment.diff(moment(), 'days');

      if (diffDays < 0) return 'Đã hết hạn';
      if (diffDays <= 30) return 'Sắp hết hạn';
      return 'Còn hiệu lực';
    },

    formatDate(date) {
      if (!date) return '—';
      return moment(date).format('DD/MM/YYYY');
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.warranty-detail-list {
  font-size: 14px;
  padding-left: 0;
  list-style: none;
}

.warranty-detail-list li {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.warranty-detail-list li:last-child {
  border-bottom: none;
  margin-bottom: 0 !important;
}
</style>
